import { SharedPermissionService, requireDefined } from '@agroone-front/shared'
import {
  Farm,
  FarmAgroCharterSignatureForm,
  FarmCooperativesDto,
  FarmCsrCertification,
  FarmFieldsChangeHistoryDto,
  MsSqlPaginatedData,
  PdfDocument,
  SaveFarm,
  UserPermissions,
} from '@agroone/entities'
import { HttpClient, HttpParams } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { environment } from '@front-app-environments/environment'
import { Store } from '@ngrx/store'
import { debounceTime, map, mergeMap, Observable, of, switchMap, take, tap } from 'rxjs'
import { FarmState } from '../../../scene/crop-management/farm/store/reducers'
import { selectFarmById } from '../../../scene/crop-management/farm/store/selectors/farm.selectors'

@Injectable({
  providedIn: 'root',
})
export class FarmService {
  private farmStore = inject(Store<FarmState>)
  private http = inject(HttpClient)
  private permissionService = inject(SharedPermissionService)
  private farmApiUrl = `${environment.apiUrl}${environment.farms}`

  public getCooperatives(cropTypeId?: number): Observable<FarmCooperativesDto[]> {
    const params: Record<string, string> = {}
    if (cropTypeId) {
      params['cropTypeId'] = cropTypeId.toString()
    }
    return this.http
      .get<MsSqlPaginatedData<FarmCooperativesDto>>(`${this.farmApiUrl}/cooperatives`, { params })
      .pipe(map((v: MsSqlPaginatedData<FarmCooperativesDto>) => v.data))
  }

  /**
   * Get cooperatives defined on a specific farm.
   */
  public getDefinedCooperatives(farmId: number, cropTypeId?: number): Observable<FarmCooperativesDto[]> {
    const params: Record<string, string> = {}
    if (cropTypeId) {
      params['cropTypeId'] = cropTypeId.toString()
    }
    return this.http
      .get<MsSqlPaginatedData<FarmCooperativesDto>>(`${this.farmApiUrl}/${farmId}/defined-cooperatives`, { params })
      .pipe(map((v: MsSqlPaginatedData<FarmCooperativesDto>) => v.data))
  }

  public getMine(): Observable<Farm> {
    return this.http.get<Farm>(`${this.farmApiUrl}/mine`)
  }

  /**
   * Get farm by id and region
   * first from the  farm state, else from the remote serve
   *
   * @param id
   * @param region
   * @returns
   */
  public get(id: number, region: string): Observable<Farm> {
    let params: HttpParams = new HttpParams()
    params = params.append('region', region)

    const options: {
      params: HttpParams
    } = {
      params,
    }

    return this.farmStore.select(selectFarmById(id)).pipe(
      take(1),
      debounceTime(200),
      mergeMap((state) => {
        if (state) {
          return of(state)
        }
        return this.http.get<Farm>(`${this.farmApiUrl}/${id}`, options).pipe(map((farm: Farm) => new Farm(farm)))
      }),
    )
  }

  /**
   * Return all farms
   *
   * @param onlyActive
   * @returns
   */
  public getAll(
    onlyActive = true,
    otherParams?: { [key: string]: string | number | boolean | Array<number> },
    callback?: (farms: MsSqlPaginatedData<Farm>) => void,
  ): Observable<Farm[]> {
    const options: { params: { filter?: string } & { [key: string]: string | number | boolean | Array<number> } } = {
      params: {},
    }
    if (onlyActive) {
      options.params.filter = 'active=1'
    }
    if (otherParams) {
      options.params = {
        ...options.params,
        ...otherParams,
      }
    }
    return of(
      this.permissionService.isGranted([
        UserPermissions.FARMS_VIEW,
        // UserPermissions.THIRD_PARTY_APPLICATOR_TASK_EDIT,
        UserPermissions.WORKLIST_EDIT,
        UserPermissions.PLANTING_MODULE_ACCESS,
        UserPermissions.USER_MODULE_ACCESS,
      ]),
    ).pipe(
      switchMap((hasPermission) => {
        if (hasPermission) {
          return this.http.get<MsSqlPaginatedData<Farm>>(this.farmApiUrl, options).pipe(
            tap((paginatedFarms) => {
              if (callback) {
                callback(paginatedFarms)
              }
            }),
            map((paginatedFarms) => {
              if (paginatedFarms?.data?.length) {
                return paginatedFarms.data
                  .sort((x, y) => requireDefined(x.farmName, 'the farm farm name').localeCompare(String(y.farmName)))
                  .map((g) => {
                    const farm = new Farm(g)
                    requireDefined(farm.contacts, 'the farm contacts').sort((x, y) => x.priority - y.priority)
                    return farm
                  })
              }
              return paginatedFarms?.data
            }),
          )
        } else {
          return of([])
        }
      }),
    )
  }

  /**
   * Save farm on create or update
   *
   * @param farm {SaveFarm}
   * @returns { Observable<Farm>}
   */
  public save(farm: SaveFarm): Observable<Farm> {
    if (farm.id) {
      return this.http.put<Farm>(this.farmApiUrl, farm)
    }

    return this.http.post<Farm>(this.farmApiUrl, farm)
  }

  public getAllFarmCsrCertification(): Observable<FarmCsrCertification[]> {
    return this.http.get<FarmCsrCertification[]>(`${environment.apiUrl}${environment.csr}/certifications`)
  }

  public getCommitmentCharter(): Observable<PdfDocument> {
    return this.http.get<PdfDocument>(`${this.farmApiUrl}/commitment-charter`)
  }

  public getSignedAgroCharter(farmId: number): Observable<PdfDocument> {
    return this.http.get<PdfDocument>(`${this.farmApiUrl}/${farmId}/signed-agro-charter`)
  }

  public signCharter(id: number, form: FarmAgroCharterSignatureForm): Observable<PdfDocument> {
    return this.http.post<PdfDocument>(`${this.farmApiUrl}/${id}/sign-charter`, {
      ...form,
    })
  }

  public deleteSignedAgroCharter(farmId: number): Observable<void> {
    return this.http.delete<void>(`${this.farmApiUrl}/${farmId}/signed-agro-charter`)
  }

  public getFarmFieldsChangeHistories(farmId: number): Observable<FarmFieldsChangeHistoryDto[]> {
    return this.http.get<FarmFieldsChangeHistoryDto[]>(`${this.farmApiUrl}/${farmId}/fields-change-history`)
  }
}
